import { gql, useApolloClient } from "@apollo/client";
import classNames from "classnames";
import React, { MutableRefObject, useState } from "react";
import { Link } from "react-router-dom";

import { PrimaryButton } from "~/components/common/buttons";
import { FundEntityOverviewFragment } from "~/components/Overview/OverviewPage.generated";
import { useCreateSubpageMutation } from "~/components/Overview/SubpagesMenu.generated";

export const SUBPAGE_MENU_FRAGMENT = gql`
  fragment SubpagesMenu on SubpageInterface {
    id
    slug
    title
    roles {
      id
      name
    }
    groups {
      id
      name
    }
  }
`;

export const CREATE_SUBPAGE_MUTATION = gql`
  mutation CreateSubpage(
    $subjectId: ID!
    $subjectType: String!
    $title: String!
  ) {
    createSubpage(
      subjectId: $subjectId
      subjectType: $subjectType
      title: $title
    ) {
      subpage {
        ... on Subpage {
          id
          title
          slug
          roles {
            id
            name
          }
          groups {
            id
            name
          }
        }
        ... on HoldCoSubpage {
          id
          title
          slug
          roles {
            id
            name
          }
          groups {
            id
            name
          }
        }
      }
    }
  }
`;

interface SubpagesMenuProps {
  subject: FundEntityOverviewFragment;
  subpages: FundEntityOverviewFragment["subpages"];
  currentSubpageSlug: String;
  parentNodeRef: MutableRefObject<any>;
  isSubpageEditing: boolean;
  isDealRoomAdmin: boolean;
}

export const SubpagesMenu = ({
  subject,
  subpages,
  currentSubpageSlug,
  parentNodeRef,
  isSubpageEditing,
  isDealRoomAdmin,
}: SubpagesMenuProps) => {
  const client = useApolloClient();
  const headerIsVisible = true;
  // const [headerIsVisible, setHeaderIsVisible] = useState(true);
  // const [windowResized, setWindowResized] = useState(false);
  const [isAddingNewPage, setIsAddingNewPage] = useState(false);
  const [newSubpageTitle, setNewSubpageTitle] = useState(""); // State for new subpage title
  const [createSubpage] = useCreateSubpageMutation({
    onCompleted: (data) => {
      if (data?.createSubpage?.subpage) {
        const newSubpage = data.createSubpage.subpage;

        // Update the Apollo cache
        client.cache.modify({
          id: client.cache.identify(subject), // Identify the subject in the cache
          fields: {
            subpages(existingSubpages = []) {
              // Append the new subpage to the existing subpages
              return [...existingSubpages, newSubpage];
            },
          },
        });
        setNewSubpageTitle("");
        setIsAddingNewPage(false);
      }
    },
  });

  const handleAddNewPage = async () => {
    const title = newSubpageTitle.trim();
    if (title) {
      try {
        await createSubpage({
          variables: {
            subjectId: subject.id,
            subjectType: subject.__typename,
            title,
          },
        });
      } catch (error) {
        console.error("Error creating subpage:", error);
      }
    }
  };

  // const handleScroll = useCallback(() => {
  //   const scrollFromTop =
  //     parentNodeRef && parentNodeRef.current.getBoundingClientRect().top;
  //   setHeaderIsVisible(scrollFromTop > 0);
  // }, [parentNodeRef]);
  //
  // const handleResize = useCallback(
  //   () => setWindowResized(!windowResized),
  //   [windowResized]
  // );

  /**
   * When position becomes fixed, the width can change. This method is used to keep the width consistent when the
   * element becomes fixed/sticky
   *
   */
  const calculateWidth = () => {
    if (parentNodeRef.current) {
      const width = parentNodeRef.current.childNodes[1].clientWidth;
      const nodeStyle = window.getComputedStyle(
        parentNodeRef.current.childNodes[1]
      );
      const paddingRight = parseInt(
        nodeStyle.getPropertyValue("padding-right"),
        10
      );
      const paddingLeft = parseInt(
        nodeStyle.getPropertyValue("padding-left"),
        10
      );

      return {
        width,
        paddingRight: `${paddingRight + paddingLeft}px`,
      };
    }
    return {};
  };

  // useEffect(() => {
  //   window.addEventListener("scroll", handleScroll);
  //   window.addEventListener("resize", handleResize);
  //   return () => {
  //     window.removeEventListener("scroll", handleScroll);
  //     window.removeEventListener("resize", handleResize);
  //   };
  // }, [handleResize, handleScroll, windowResized]);

  return (
    <div
      className={classNames({ "sticky-spe": !headerIsVisible })}
      style={calculateWidth()}
    >
      <div className="d-flex justify-content-between align-items-center font-weight-light text-uppercase fs-sm mb-2 pl-3 mt-2">
        <span>Table of contents</span>
      </div>
      <div
        className="nav flex-column nav-pills"
        id="v-pills-tab"
        role="tablist"
      >
        {subpages?.map((subpage) => (
          <Link
            key={`l-${subpage.id}`}
            to={`/overview/${subpage.slug}`}
            className={classNames("nav-link", {
              active: subpage.slug === currentSubpageSlug,
              disabled: isSubpageEditing,
            })}
            onClick={(e) => isSubpageEditing && e.preventDefault()}
          >
            {subpage.title}
          </Link>
        ))}
      </div>
      {isDealRoomAdmin && (
        <div className="mb-2 pl-3">
          {!isAddingNewPage ? (
            <button
              className="btn btn-link text-primary p-0"
              onClick={() => setIsAddingNewPage(true)}
            >
              + Add New Page
            </button>
          ) : (
            <div className="d-flex align-items-center">
              <input
                type="text"
                className="form-control"
                placeholder="Enter page title"
                value={newSubpageTitle} // Bind input value to state
                onChange={(e) => setNewSubpageTitle(e.target.value)} // Update state on input change
                autoFocus
              />
              <PrimaryButton
                className="btn btn-primary ml-2 d-flex align-items-center"
                onClick={handleAddNewPage}
              >
                Save
              </PrimaryButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
